/**
 * 顔検出コンポーネント
 * カメラを使用してユーザーの顔を検出し、セッションを自動開始する
 */
import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import { useAtom } from "jotai/index";

// ルートパスからのインポート
import "~/css/FaceDetection.css";
import {
  isFaceAtom,
  isFaceDetectionAtom,
  isOutputTextAtom,
  standbyModeAtom,
  threshFaceSizeAtom,
  isSettingOpenAtom,
  isAllLoadedAtom,
  faceModelLoadedAtom,
} from "~/Atom";

// 時間関連の定数（ミリ秒）
const timeConstants = {
  resetInterval: 5000, // 5秒以上経過するとカウントリセット
  detectionInterval: 500, // 検出間隔
  requiredDetectionTime: 2000, // 検出として判定する時間
};

// 顔検出関連の定数
const faceDetection = {
  requiredDetections: Math.floor(
    timeConstants.requiredDetectionTime / timeConstants.detectionInterval
  ), // 2秒で必要な検出回数
  maxMissedDetections: Math.floor(timeConstants.resetInterval / timeConstants.detectionInterval), // 検出されない場合にリセットする回数
};

// スタンバイモード定数
const standbyModes = {
  stopped: 0,
  active: 1,
};

// ビデオ設定
const videoConstraints = {
  width: 320,
  height: 240,
  facingMode: "user",
};

// 表示要素の設定
const display = {
  visible: "visible" as const,
  hidden: "hidden" as const,
  boxColor: {
    detected: "lightGreen",
    notDetected: "red",
  },
  decimalPlaces: 2,
};

/**
 * 顔検出コンポーネント
 * WebカメラからリアルタイムでFace APIを用いて顔検出を行う
 */
const FaceDetection: React.FC = () => {
  // Refオブジェクト
  const webcamRef = useRef<Webcam>(null);
  const isModelLoaded = useRef<boolean>(false);
  // ブラウザのsetIntervalはnumberを返すためブラウザ向けにnumberを使う
  const intervalRef = useRef<number | null>(null);

  // ローカル状態
  const [box, setBox] = useState<faceapi.Box | null>(null);
  const [sizeLog, setSizeLog] = useState<string | null>(null);
  const [, setDetectionCount] = useState<number>(0);
  const [, setMissedCount] = useState<number>(0);

  // グローバル状態（Jotai）
  const [isFaceDetection] = useAtom(isFaceDetectionAtom);
  const [isFace, setIsFace] = useAtom(isFaceAtom);
  const [threshFaceSize] = useAtom(threshFaceSizeAtom);
  const [isOutputText] = useAtom(isOutputTextAtom);
  const [standbyMode] = useAtom(standbyModeAtom);
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const [isAllLoaded] = useAtom(isAllLoadedAtom);
  const [, setFaceModelLoaded] = useAtom(faceModelLoadedAtom);

  /**
   * 顔検出用モデルの読み込み
   */
  const loadModels = async () => {
    const basePath = "faceapi/weights/";

    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(basePath),
      faceapi.nets.faceLandmark68Net.loadFromUri(basePath),
      faceapi.nets.faceRecognitionNet.loadFromUri(basePath),
    ]);
    isModelLoaded.current = true;
    // 顔認識モデル読み込み完了
    setFaceModelLoaded(true);
  };

  /**
   * 顔検出の定期実行を開始
   */
  const startDetection = () => {
    if (intervalRef.current) return; // すでに開始されている場合は何もしない

    intervalRef.current = window.setInterval(() => {
      handleDetectFaces();
    }, timeConstants.detectionInterval);
  };

  /**
   * 顔検出の定期実行を停止
   */
  const stopDetection = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  /**
   * コンポーネントマウント時の初期化
   */
  useEffect(() => {
    let mounted = true;

    const loadAndDetect = async () => {
      // モデル読み込み前のマウント状態チェック
      if (!mounted) return;

      if (!isModelLoaded.current) {
        await loadModels();
        // モデル読み込み完了後のマウント状態チェック
        if (!mounted) return;
      }

      // startDetection実行直前の最終マウント状態チェック
      if (!mounted) return;
      startDetection();
    };

    loadAndDetect();

    // アンマウント時のクリーンアップを確実に行う
    return () => {
      mounted = false;
      stopDetection();
    };
  }, []);

  /**
   * 顔検出のメイン処理
   * カメラ映像から顔を検出し、サイズに応じた処理を行う
   */
  const handleDetectFaces = async () => {
    try {
      const hasRequiredResources =
        !isOutputText && webcamRef.current && webcamRef.current.video && isModelLoaded.current;

      if (!hasRequiredResources) return;

      // null チェックを追加して型安全性を確保
      const video = webcamRef.current?.video;
      if (!video) return;

      // 顔検出の実行
      const detections = await faceapi
        .detectAllFaces(
          video, // nullでないことが確認されたvideoを渡す
          new faceapi.TinyFaceDetectorOptions()
        )
        .withFaceLandmarks()
        .withFaceDescriptors();

      // 検出結果の検証
      const validDetections = detections.filter(
        (det) =>
          det &&
          det.detection &&
          det.detection.box &&
          det.detection.box.x !== null &&
          det.detection.box.y !== null &&
          det.detection.box.width !== null &&
          det.detection.box.height !== null
      );

      if (validDetections.length > 0) {
        // 最大の顔矩形を選択
        const largestBox = detections.reduce((maxBox, detection) => {
          const box = detection.detection.box;
          return box.width * box.height > maxBox.width * maxBox.height ? box : maxBox;
        }, detections[0].detection.box);

        processDetectedFace(largestBox);
      } else {
        handleNoFaceDetected();
      }
    } catch (error) {
      setBox(null);
      setSizeLog(null);

      console.error("Face detection error:", error);
    }
  };

  /**
   * 検出された顔の処理
   *
   * @param detectedBox - 検出された顔の矩形
   */
  const processDetectedFace = (detectedBox: faceapi.Box) => {
    const video = webcamRef.current?.video;
    if (!video) return;

    const { videoWidth, videoHeight } = video;
    const webcamRect = video.getBoundingClientRect();

    // ボックスの位置とサイズを調整
    const adjustedBox = new faceapi.Box({
      x: (detectedBox.x * webcamRect.width) / videoWidth,
      y: (detectedBox.y * webcamRect.height) / videoHeight,
      width: (detectedBox.width * webcamRect.width) / videoWidth,
      height: (detectedBox.height * webcamRect.height) / videoHeight,
    });

    setBox(adjustedBox);

    // 矩形サイズのログ
    const boxSize = `${adjustedBox.width.toFixed(display.decimalPlaces)}x${adjustedBox.height.toFixed(display.decimalPlaces)}`;
    setSizeLog(boxSize);

    // 閾値と比較
    const isFaceDetectedBySize =
      adjustedBox.width > threshFaceSize || adjustedBox.height > threshFaceSize;

    if (isFaceDetectedBySize) {
      handleFaceDetected();
    } else {
      updateMissedCount();
    }
  };

  /**
   * 顔が検出された時の処理
   */
  const handleFaceDetected = () => {
    // 読み込みが完了するまで処理しない
    if (!isAllLoaded) return;
    setDetectionCount((prevCount) => {
      const newCount = prevCount + 1;
      setMissedCount(0); // 検出された場合はカウントリセット

      const shouldActivateFace =
        standbyMode > standbyModes.stopped &&
        newCount >= faceDetection.requiredDetections &&
        !isFace &&
        isFaceDetection;

      if (shouldActivateFace) {
        setIsFace(true);
      }

      return newCount;
    });
  };

  /**
   * 顔が検出されなかった時の処理
   */
  const handleNoFaceDetected = () => {
    setBox(null);
    setDetectionCount((prevCount) => (prevCount > 0 ? prevCount : 0));
    updateMissedCount();
  };

  /**
   * 検出失敗カウントの更新
   * isFace === trueの場合、maxMissedDetectionsに達したらisFaceをfalseにしてカウントをリセット
   * isFace === falseの場合、missedCountは増加し続けるが、次の顔検出でhandleFaceDetectedによりリセット
   */
  const updateMissedCount = () => {
    setMissedCount((prevCount) => {
      const newCount = prevCount + 1;

      if (newCount >= faceDetection.maxMissedDetections && isFace) {
        setDetectionCount(0); // カウントリセット
        setIsFace(false);
        return 0; // missedCount をリセット
      }

      return newCount;
    });
  };

  /**
   * 顔検出の状態変化に応じた検出処理のリセット
   */
  useEffect(() => {
    const resetDetection = async () => {
      await stopDetection();
      await startDetection();
    };

    if (standbyMode > standbyModes.stopped) {
      resetDetection();
    }
  }, [isFaceDetection, isFace, standbyMode, isAllLoaded]);

  /**
   * カメラのエラーハンドリング
   */
  const handleWebcamError = (error: string | DOMException) => {
    console.error("Webcam error:", error);
  };

  /**
   * 表示状態の判定
   */
  const getVisibilityState = (): "visible" | "hidden" => {
    // 顔検出が有効、かつ、設定画面を開いている間、カメラ映像を表示
    return isFaceDetection && isSettingOpen ? display.visible : display.hidden;
  };

  /**
   * サイズログ表示のvisibility判定
   */
  const getSizeLogVisibility = (): "visible" | "hidden" => {
    // 顔検出が有効、かつ、設定画面を開いている間、サイズログを表示
    return isFaceDetection && isSettingOpen ? display.visible : display.hidden;
  };

  /**
   * 検出ボックスのスタイル判定
   */
  const getBoxStyle = () => {
    if (!box) return {};

    const isDetectedBySize = box.width > threshFaceSize || box.height > threshFaceSize;

    const borderColor = isDetectedBySize ? display.boxColor.detected : display.boxColor.notDetected;

    return {
      position: "absolute" as const,
      border: `2px solid ${borderColor}`,
      left: `${box.x}px`,
      top: `${box.y}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      visibility: getVisibilityState(),
    };
  };

  return (
    <div className={`FaceDetection ${isFaceDetection ? "" : "hide"}`}>
      <Webcam
        audio={false}
        ref={webcamRef}
        className="webcam"
        videoConstraints={{
          width: videoConstraints.width,
          height: videoConstraints.height,
          facingMode: videoConstraints.facingMode,
        }}
        onUserMediaError={handleWebcamError}
        style={{ visibility: getVisibilityState() }}
      />

      {box && <div className="box" style={getBoxStyle()} />}

      {sizeLog && (
        <div className="sizeLog" style={{ visibility: getSizeLogVisibility() }}>
          Size: {sizeLog}
        </div>
      )}
    </div>
  );
};

export default FaceDetection;
